<template>
	<app-title-bar
		p-title-bar
		:icon="['fas', 'fa-clipboard-check']"
		subtitle="Yard App"
		title="Stock Check Detail"
		:badges="[contract?.contract_number, contract?.account_name]">
		<app-button to="/yard-app/stock-items" icon="fa-circle-left"> Back </app-button>
	</app-title-bar>

	<app-panel title="Items">
		<template #header>
			<div class="flex gap-2">
				<app-button size="small" :disabled="loading" @click="confirmQuantities">
					Confirm Quantities
				</app-button>

				<app-badge>{{ contract?.contract_number }}</app-badge>
				<app-badge>{{ contract?.account }}</app-badge>
			</div>
		</template>

		<stock-check-detail-table
			:items="items"
			:loading="loading"
			@updated-items="updated"
			@updated-note="(item) => updatedNotes(item)"
			@clicked-item="(value: ContractItem) => (currentItem = value)" />

		<div class="flex justify-between mt-5 mb-10 gap-3">
			<app-button icon="fa-plus" @click="adding = true">Add Item Ancillaries</app-button>
			<app-button icon="fa-save" class="ml-auto" @click="updateItems(true)">Save</app-button>
			<app-button icon="fa-truck" @click="sendInterceptor">Send to Hire Desk</app-button>
		</div>

		<item-availability-table :contract-item="currentItem" show-title current-depot />
	</app-panel>

	<confirm-dialog
		:open="qtyDiscrepancyWarning"
		@confirmed="send"
		@canceled="qtyDiscrepancyWarning = false">
		Warning: Quantity discrepancies exist. Are you sure you want to Send to Hiredesk?
	</confirm-dialog>

	<slideover
		title="Ancillary Items"
		:open="adding"
		expandable
		:actions="[{ text: 'Done', method: close }]"
		@close="adding = false">
		<app-input
			v-model="additionalItems"
			label="Additional Items"
			name="additional_items"
			type="textarea" />
	</slideover>
</template>

<script lang="ts" setup>
	import { ref, onMounted } from 'vue'
	import StockCheckDetailTable from '@/components/datatables/yard-app/stock/StockCheckDetailTable.vue'
	import type { ContractItem } from '@/data/schema/contract-item'
	import useContracts from '@/composables/contracts'
	import { formatItemsDates } from '@/utils/date'
	import { useRoute } from 'vue-router'
	import useToasterStore from '@/stores/toaster'
	import type { Contract } from '@/data/schema/contract'
	import type { StatusUpdate } from '@/data/interfaces/status-update'
	import ContractService from '@/services/contract'
	import useAuthStore from '@/stores/auth'
	import { statusParam } from '@/composables/contracts'
	import { getAllItems, updateStatusParamOnItems } from '@/utils/contract-items'
	import ItemAvailabilityTable from '@/components/datatables/all/ItemAvailabilityTable.vue'
	import { ContractItemStatus } from '@/data/schema/contract-item-status'

	// Resources
	const api = new ContractService()
	const route = useRoute()
	const toaster = useToasterStore()
	const authStore = useAuthStore()

	// Data
	const contractNumber = ref()
	const loading = ref(true)
	const contract = ref<Contract>()
	const items = ref<ContractItem[]>([])
	const currentItem = ref()
	const adding = ref(false)
	const additionalItems = ref('')
	const qtyDiscrepancyWarning = ref(false)

	/**
	 * Set all confirmed quantities to
	 * required quantity values
	 */
	function confirmQuantities() {
		updateStatusParamOnItems(
			items.value,
			'stock-check',
			'quantity_confirmed',
			'quantity_required',
			true
		)
	}

	/**
	 * Get contract and contract item data
	 */
	async function getData() {
		loading.value = true

		const { fetchData } = useContracts()

		try {
			const data = await fetchData(contractNumber.value, {
				withItems: false,
				withStockCheckItems: true,
				withContractStatus: true,
				withStockCheckStatus: true,
				operatorId: authStore.user?.id
			})
			contract.value = data.contract
			items.value = formatItemsDates(data.items)

			if (
				contract.value.status &&
				contract.value.status[0] &&
				contract.value.status[0].item_notes
			) {
				additionalItems.value = contract.value.status[0].item_notes
			}

			loading.value = false
		} catch (error) {
			console.error(error)
			loading.value = false
		}
	}

	/**
	 * Items have been updated
	 */
	function updated(item: ContractItem) {
		if (!item) {
			getData()
		} else {
			const index = items.value.findIndex((i) => i.id === item.id)

			if (index !== -1) {
				items.value[index].changed = true
			}
		}
	}

	function close() {
		adding.value = false
	}

	/**
	 * Bulk Update all Items
	 */
	function updateItems(save = false) {
		if (save) {
			toaster.working({
				text: 'Saving changes...',
				dismissable: false,
				timeout: 0
			})
		}

		const saveItems = getAllItems(items.value).map((item) => ({
			id: item.id,
			status_id: item.stock_check_status?.[0].id,
			quantity_confirmed: item.stock_check_status?.[0].quantity_confirmed
		}))

		api.bulkUpdateStatus(saveItems as StatusUpdate[], 'stock-status', save)
			.then(() => {
				toaster.clear()
				toaster.success({ text: 'Contract saved successfully!' })
				getData()
			})
			.catch((error: any) => {
				toaster.clear()
				toaster.error({ text: 'Error occurred updating contract items' })
				console.error(error)
			})
	}

	function updatedNotes(item: ContractItem) {
		const updateStockCheckStatusNotes = (
			stockCheckStatus: ContractItemStatus[],
			notes: string
		) => {
			if (stockCheckStatus.length > 0) {
				stockCheckStatus[0].notes = notes
				stockCheckStatus[0].notes_status = 4
			}
		}
		const itemIndex = items.value.findIndex((i) => i.id === (item.component_parent || item.id))
		const componentIndex = items.value[itemIndex].kit_components?.findIndex(
			(i) => i.id === item.id
		)
		if (itemIndex === -1) {
			return
		}
		if (componentIndex == null || componentIndex === -1) {
			updateStockCheckStatusNotes(
				items.value[itemIndex].stock_check_status!,
				item.stock_check_status![0].notes!
			)
		} else {
			updateStockCheckStatusNotes(
				items.value[itemIndex].kit_components![componentIndex!].stock_check_status!,
				item.stock_check_status![0].notes!
			)
		}
	}

	function sendInterceptor() {
		let quantitiesMatch = true

		quantitiesMatch = getAllItems(items.value).every((item) => {
			return (
				statusParam(item, 'quantity_required', 'stock-check') ===
				statusParam(item, 'quantity_confirmed', 'stock-check')
			)
		})

		quantitiesMatch ? send() : (qtyDiscrepancyWarning.value = true)
	}

	/**
	 * Save additional items to item notes
	 * Save contract items quantity values
	 */
	function send() {
		qtyDiscrepancyWarning.value = false

		toaster.working({
			text: 'Sending to hire desk...',
			dismissable: false,
			timeout: 0
		})

		if (contract.value) {
			api.updateStatus(contract.value.contract_number, contract.value.status[0].id, {
				item_notes: additionalItems.value
			})
				.then(() => {
					updateItems()
				})
				.catch((error: any) => {
					toaster.clear()
					toaster.error({ text: 'Error occurred updating contract' })
					console.error(error)
				})
		}
	}

	onMounted(() => {
		contractNumber.value = route.params.id

		if (contractNumber.value) {
			getData()
		}
	})
</script>
